import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatRelativeTime } from "../../utils";
import ProjectStack from './ProjectStack';
import './WorkspaceStack.css';

const WorkspaceStack = ({
  workspace,
  projects,
  expandedWorkspaces,
  toggleWorkspaceExpansion,
  expandedProjects,
  toggleProjectExpansion,
  stackedJobs,
  expandedStacks,
  toggleStackExpansion,
  searchTerm,
  hasActiveFilters,
  openAiDialogForJob,
  renderStackedImageIcon,
  selectedRow,
  TASK_STATUSES,
  ALL_TASKS_TAB,
  handleRowClick,
  handleRowDoubleClick,
  handleAddFromRow,
  handleEdit,
  handleDelete,
  handleViewAttachments,
  handleReplyToNote,
  handlePriorityClick,
  handlePriorityMouseEnter,
  handlePriorityMouseLeave,
  handleNoteTextMouseEnter,
  handleNoteTextMouseLeave,
  handleLinkedNoteClick,
  handleLinkedNoteMouseEnter,
  handleLinkedNoteMouseLeave,
  isNoteReply,
  getReplyNoteId,
  isOriginalNoteExists,
  userStatusMap,
  loadingUsers,
  getPriorityValue,
  manuallyUpdatedPriorities,
  shouldShowNotePopup,
  userId,
  tasksByJob,
  loadingTasksByJob,
  expandedTasks,
  setExpandedTasks,
  toggleTaskExpansion,
  notesByTask,
  loadingNotesByTask,
  fetchTasksForJob,
  fetchNotesForTask,
  onTaskEdit,
  onTaskDelete,
  onTaskStatusChange,
  onTaskClick,
  refreshTasksForJob,
  onFilterByProject,
  onFilterByWorkspace,
  refreshNotes,
  highlightedTaskId,
  clearHighlightedTask,
  loadMoreTaskNotes,
  taskPagination,
  onOpenEdit,
  onCreateTask,
  onOpenSlideshow,
  activeTabByJob,
  onTabChange,
  assigneeFilters,
  jobUsers,
  loadingUsersByJob,
  onAssigneeFilter,
  clearAssigneeFilter,
  fetchJobPermissionUsers,
  workspaceRole,
  onDenyUserClick,
  fetchJobTaskUsers,
  jobUsersTask,
  loadingUsersByJobTask,
  onTaskEdit: onTaskEditWrapper,
  workspacesMetadata,
  onRefreshWorkspacesMetadata,
  onProjectAdded,
  selectedFilters,
  onPriorityUpdate,
  defWorkId,
  onOpenModal,
}) => {
  // Derived state – current drilldown level
  const isExpanded = expandedWorkspaces[workspace.workspaceId];
  const expandedProjectId = Object.keys(expandedProjects).find(
    (pid) => expandedProjects[pid] && projects.some((p) => p.projectId.toString() === pid)
  );
  const expandedProject = expandedProjectId
    ? projects.find((p) => p.projectId.toString() === expandedProjectId)
    : null;

  const expandedProjectJobs = useMemo(() => {
    if (!expandedProject) return [];
    return (stackedJobs || []).filter((job) => job && job.projectId === expandedProject.projectId);
  }, [expandedProject, stackedJobs]);

  const isAdmin = useMemo(() => {
    return workspaceRole === 'admin' || workspaceRole === 1 ;
  }, [workspaceRole]);

  const expandedJob = useMemo(() => {
    if (!expandedProject) return null;
    return expandedProjectJobs.find(
      (job) => expandedStacks && job && job.jobName && expandedStacks[job.jobName]
    );
  }, [expandedProject, expandedProjectJobs, expandedStacks]);

  // Find the currently expanded task (if any) and its title
  const [expandedTaskTitle, setExpandedTaskTitle] = useState(null);
  useEffect(() => {
    if (!expandedTasks) return;
    const expandedTaskId = Object.keys(expandedTasks).find(id => expandedTasks[id]);
    if (expandedTaskId && expandedJob && tasksByJob[expandedJob.jobId]) {
      const task = tasksByJob[expandedJob.jobId].find(t => t.id.toString() === expandedTaskId);
      setExpandedTaskTitle(task ? task.title : null);
    } else {
      setExpandedTaskTitle(null);
    }
  }, [expandedTasks, expandedJob, tasksByJob]);

  // Counters for display
  const totalProjects = projects.length;
  const totalNotes = projects.reduce(
    (sum, project) => sum + project.jobs.reduce((s, job) => s + (job.noteCount || 0), 0),
    0
  );
  const totalJobs = projects.reduce((sum, project) => sum + (project.jobs?.length || 0), 0);

  const wsHeaderRef = useRef(null);
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (wsHeaderRef.current) {
        const height = wsHeaderRef.current.offsetHeight;
        const container = wsHeaderRef.current.closest('.ws-expanded');
        if (container) {
          container.style.setProperty('--ws-header-height', `${height}px`);
        }
      }
    };
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (wsHeaderRef.current) observer.observe(wsHeaderRef.current);
    window.addEventListener('resize', updateHeaderHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  // Latest project / job for collapsed card preview
  const latestProject = useMemo(() => {
    if (projects.length === 0) return null;
    return [...projects].sort((a, b) => {
      const aLatest = Math.max(...a.jobs.map((job) => new Date(job.latestTimeStamp || 0).getTime()));
      const bLatest = Math.max(...b.jobs.map((job) => new Date(job.latestTimeStamp || 0).getTime()));
      return bLatest - aLatest;
    })[0];
  }, [projects]);

  const latestJob = useMemo(() => {
    if (!latestProject) return null;
    return latestProject.jobs?.sort(
      (a, b) => new Date(b.latestTimeStamp || 0) - new Date(a.latestTimeStamp || 0)
    )[0];
  }, [latestProject]);

  const getCurrentJob = () => {
    if (expandedProject) {
      return expandedProject.jobs?.[0] || null;
    } else if (expandedJob) {
      return expandedJob;
    } else if (projects.length > 0) {
      const firstProject = projects[0];
      return firstProject?.jobs?.[0] || null;
    }
    return null;
  };
  const currentJob = getCurrentJob();

  const handleAddProject = useCallback(() => {
    onOpenModal('addProject', {
      workspaceId: workspace.workspaceId,
      workspaceName: workspace.workspaceName,
      existingProjects: projects
    });
  }, [onOpenModal, workspace.workspaceId, workspace.workspaceName, projects]);
  
  const handleAddJob = useCallback(() => {
    onOpenModal('addJob', {
      workspaceId: workspace.workspaceId,
      projectId: expandedProject?.projectId,
      projectName: expandedProject?.projectName
    });
  }, [onOpenModal, workspace.workspaceId, expandedProject]);
  
  const handleCreateTask = useCallback(() => {
    onOpenModal('createTask', {
      jobId: expandedJob?.jobId,
      jobName: expandedJob?.jobName
    });
  }, [onOpenModal, expandedJob]);

  const handleContextualPlusClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (expandedJob) {
      handleCreateTask();
    } else if (expandedProject) {
      handleAddJob();
    } else {
      handleAddProject();
    }
  };

  // Back navigation
  const handleBack = () => {
    const expandedTaskId = Object.keys(expandedTasks || {}).find(
      (tid) => expandedTasks[tid] && tasksByJob[expandedJob?.jobId]?.some((t) => t.id.toString() === tid)
    );
    if (expandedTaskId) {
      toggleTaskExpansion(expandedTaskId, expandedJob.jobId);
    } else if (expandedJob) {
      toggleStackExpansion(expandedJob.jobName, expandedJob.jobId);
    } else if (expandedProject) {
      toggleProjectExpansion(expandedProject.projectId);
    } else {
      toggleWorkspaceExpansion(workspace.workspaceId);
    }
  };

  const projectStackCommonProps = {
    stackedJobs,
    expandedStacks,
    toggleStackExpansion,
    searchTerm,
    hasActiveFilters,
    openAiDialogForJob,
    renderStackedImageIcon,
    selectedRow,
    TASK_STATUSES,
    ALL_TASKS_TAB,
    handleRowClick,
    handleRowDoubleClick,
    handleAddFromRow,
    handleEdit,
    handleDelete,
    handleViewAttachments,
    handleReplyToNote,
    handlePriorityClick,
    handlePriorityMouseEnter,
    handlePriorityMouseLeave,
    handleNoteTextMouseEnter,
    handleNoteTextMouseLeave,
    handleLinkedNoteClick,
    handleLinkedNoteMouseEnter,
    handleLinkedNoteMouseLeave,
    isNoteReply,
    getReplyNoteId,
    isOriginalNoteExists,
    userStatusMap,
    loadingUsers,
    getPriorityValue,
    manuallyUpdatedPriorities,
    shouldShowNotePopup,
    userId,
    tasksByJob,
    loadingTasksByJob,
    expandedTasks,
    setExpandedTasks,
    toggleTaskExpansion,
    notesByTask,
    loadingNotesByTask,
    fetchTasksForJob,
    fetchNotesForTask,
    onTaskEdit,
    onTaskDelete,
    onTaskStatusChange,
    onTaskClick,
    refreshTasksForJob,
    onFilterByProject,
    onFilterByWorkspace,
    refreshNotes,
    highlightedTaskId,
    clearHighlightedTask,
    loadMoreTaskNotes,
    taskPagination,
    onOpenEdit,
    onCreateTask,
    onOpenSlideshow,
    activeTabByJob,
    onTabChange,
    assigneeFilters,
    jobUsers,
    loadingUsersByJob,
    onAssigneeFilter,
    clearAssigneeFilter,
    fetchJobPermissionUsers,
    workspaceRole,
    onDenyUserClick,
    fetchJobTaskUsers,
    jobUsersTask,
    loadingUsersByJobTask,
    onTaskEdit: onTaskEditWrapper,
  };

  // RENDER
  const workspaceMeta = workspacesMetadata?.find((ws) => ws.workspaceId === workspace.workspaceId);
  const noteCount = workspaceMeta?.totalSiteNotes ?? workspace.totalSiteNotes ?? totalNotes;
  const lastActivityTime = workspaceMeta?.latestTimeStamp ?? workspace.latestTimeStamp ?? latestJob?.latestTimeStamp;
  const wsDescription = workspace.description ?? null;
  const wsLastUser = workspaceMeta?.lastSiteNoteUserName ?? workspace.lastSiteNoteUserName ?? latestJob?.lastSiteNoteUserName ?? null;

  // Collapsed view
  if (!isExpanded) {
    const maxLayers = Math.min(totalProjects, 5);
    return (
      <div className="ws-collapsed" onClick={() => toggleWorkspaceExpansion(workspace.workspaceId)}>
        {[...Array(maxLayers)].map((_, idx) => {
          const index = maxLayers - 1 - idx;
          const isTopCard = idx === maxLayers - 1;
          return (
            <div
              key={`workspace-layer-${index}`}
              className="ws-layer"
              style={{
                left: `${index * 15}px`,
                transform: `rotate(${index * -2}deg)`,
                zIndex: maxLayers - index,
                opacity: 1 - index * 0.15,
              }}
            >
              {isTopCard ? (
                <div className="ws-top-card">
                  <div className="ws-card-header">
                    <div className="ws-card-title">
                      <i className="fas fa-building" />
                      <span>{workspace.workspaceName}</span>
                      {workspace.workspaceFriendlyId && (
                        <span className="ws-friendly-id">{workspace.workspaceFriendlyId}</span>
                      )}
                    </div>
                    <div className="ws-badge">
                      <i className="fas fa-layer-group" />
                      <span>{noteCount} notes</span>
                    </div>
                  </div>
                  <div className="ws-card-body">
                    {wsLastUser && (
                      <div className="ws-user-chip">
                        <div className="ws-user-chip-avatar">
                          {wsLastUser.charAt(0).toUpperCase()}
                        </div>
                        <span className="ws-user-chip-name">{wsLastUser}</span>
                        {lastActivityTime && (
                          <span className="ws-user-chip-time">
                            {formatRelativeTime(lastActivityTime)}
                          </span>
                        )}
                        <span className="ws-user-chip-indicator">
                          <i className="fas fa-pen" />
                          <span className="ws-user-chip-tooltip">last edit</span>
                        </span>
                      </div>
                    )}
                    <div
                      className={`ws-description${wsDescription ? '' : ' ws-no-desc'}`}
                      title={wsDescription || 'No description available'}
                    >
                      {wsDescription || 'No description available'}
                    </div>
                    <div className="ws-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div className="ws-footer-left" style={{ display: 'flex', gap: '12px' }}>
                        <span>
                          <i className="fas fa-briefcase" /> {totalProjects} {totalProjects === 1 ? "Project" : "Projects"}
                        </span>
                      </div>
                      <div className="ws-footer-right" style={{ display: 'flex', gap: '12px' }}>
                        <button
                          className="ws-action-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (latestJob && openAiDialogForJob) openAiDialogForJob(latestJob);
                          }}
                          title="Summarize notes (AI)"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#3498db' }}
                        >
                          <i className="fas fa-comments" />
                        </button>
                        {isAdmin && (
                          <button
                            className="ws-action-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddProject();
                            }}
                            title="Add Project"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#28a745' }}
                          >
                            <i className="fas fa-plus-circle" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ws-click-hint">Click to expand workspace</div>
                </div>
              ) : (
                <div className="ws-placeholder-card">
                  <i className="fas fa-building" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Expanded view
  const hasAnyExpanded = !!(expandedProject || expandedJob || expandedTaskTitle);
  return (
    <div className="ws-expanded">
      <div className="ws-header" ref={wsHeaderRef}>
        <div className="ws-header-left">
          <div className="ws-job-info">
            <i className="fas fa-building" />
            <div>
              <span className="ws-job-name">{workspace.workspaceName}</span>
              {!hasAnyExpanded && <small className="ws-stats">{totalProjects} projects • {totalNotes} notes</small>}
            </div>
          </div>
          {expandedProject && (
            <>
              <i className="fas fa-chevron-right ws-chevron" />
              <div className="ws-breadcrumb-item">
                <i className="fas fa-folder" />
                <span>{expandedProject.projectName}</span>
              </div>
            </>
          )}
          {expandedJob && (
            <>
              <i className="fas fa-chevron-right ws-chevron" />
              <div className="ws-breadcrumb-item">
                <i className="fas fa-briefcase" />
                <span>{expandedJob.jobName}</span>
              </div>
            </>
          )}
          {/* Task breadcrumb */}
          {expandedTaskTitle && (
            <>
              <i className="fas fa-chevron-right ws-chevron" />
              <div className="ws-breadcrumb-item">
                <i className="fas fa-tasks" />
                <span>{expandedTaskTitle}</span>
              </div>
            </>
          )}
        </div>
        <div className="ws-actions">
          <button
            className="ws-action-btn"
            onClick={() => openAiDialogForJob(currentJob)}
            title="Summarize notes (AI)"
            disabled={!currentJob}
          >
            <i className="fas fa-comments" />
          </button>

          {isAdmin && (
            <button
              className="ws-action-btn ws-contextual-plus"
              onClick={handleContextualPlusClick}
              title={
                expandedJob
                  ? "Create task"
                  : expandedProject
                  ? "Add job"
                  : "Add project"
              }
              type="button"
            >
              <i className="fas fa-plus-circle" />
            </button>
          )}

          <button className="ws-back-btn" onClick={handleBack} title="Back">
            <i className="fas fa-arrow-left" />
          </button>
        </div>
      </div>
      <div className="ws-projects-area">
        <div className={hasAnyExpanded ? "ws-projects-block" : "ws-projects-grid"}>
          {expandedProject ? (
            <ProjectStack
              key={`project-${expandedProject.projectId}`}
              project={expandedProject}
              expandedProjects={expandedProjects}
              toggleProjectExpansion={toggleProjectExpansion}
              hideHeader={true}
              selectedFilters={selectedFilters}
              onPriorityUpdate={onPriorityUpdate}
              {...projectStackCommonProps}
              onOpenSlideshow={onOpenSlideshow}
              isAdmin={isAdmin}
            />
          ) : (
            projects.map((project) => (
              <ProjectStack
                key={`project-${project.projectId}`}
                project={project}
                expandedProjects={expandedProjects}
                toggleProjectExpansion={toggleProjectExpansion}
                hideHeader={false}
                {...projectStackCommonProps}
                onOpenSlideshow={onOpenSlideshow}
                isAdmin={isAdmin}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

WorkspaceStack.propTypes = {
  workspace: PropTypes.object.isRequired,
  projects: PropTypes.array.isRequired,
  expandedWorkspaces: PropTypes.object.isRequired,
  toggleWorkspaceExpansion: PropTypes.func.isRequired,
  expandedProjects: PropTypes.object.isRequired,
  toggleProjectExpansion: PropTypes.func.isRequired,
  stackedJobs: PropTypes.array,
  expandedStacks: PropTypes.object,
  toggleStackExpansion: PropTypes.func,
  searchTerm: PropTypes.string,
  hasActiveFilters: PropTypes.bool,
  openAiDialogForJob: PropTypes.func,
  renderStackedImageIcon: PropTypes.func,
  selectedRow: PropTypes.any,
  TASK_STATUSES: PropTypes.any,
  ALL_TASKS_TAB: PropTypes.any,
  handleRowClick: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleAddFromRow: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleViewAttachments: PropTypes.func,
  handleReplyToNote: PropTypes.func,
  handlePriorityClick: PropTypes.func,
  handlePriorityMouseEnter: PropTypes.func,
  handlePriorityMouseLeave: PropTypes.func,
  handleNoteTextMouseEnter: PropTypes.func,
  handleNoteTextMouseLeave: PropTypes.func,
  handleLinkedNoteClick: PropTypes.func,
  handleLinkedNoteMouseEnter: PropTypes.func,
  handleLinkedNoteMouseLeave: PropTypes.func,
  isNoteReply: PropTypes.func,
  getReplyNoteId: PropTypes.func,
  isOriginalNoteExists: PropTypes.func,
  userStatusMap: PropTypes.object,
  loadingUsers: PropTypes.object,
  getPriorityValue: PropTypes.func,
  manuallyUpdatedPriorities: PropTypes.object,
  shouldShowNotePopup: PropTypes.func,
  userId: PropTypes.number,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  expandedTasks: PropTypes.object,
  toggleTaskExpansion: PropTypes.func,
  notesByTask: PropTypes.object,
  loadingNotesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  onTaskEdit: PropTypes.func,
  onTaskDelete: PropTypes.func,
  onTaskStatusChange: PropTypes.func,
  onTaskClick: PropTypes.func,
  refreshTasksForJob: PropTypes.func,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  onOpenEdit: PropTypes.func,
  onCreateTask: PropTypes.func,
  onOpenSlideshow: PropTypes.func,
  activeTabByJob: PropTypes.object,
  onTabChange: PropTypes.func,
  assigneeFilters: PropTypes.object,
  jobUsers: PropTypes.object,
  loadingUsersByJob: PropTypes.object,
  onAssigneeFilter: PropTypes.func,
  clearAssigneeFilter: PropTypes.func,
  fetchJobPermissionUsers: PropTypes.func,
  workspaceRole: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDenyUserClick: PropTypes.func,
  fetchJobTaskUsers: PropTypes.func,
  jobUsersTask: PropTypes.object,
  loadingUsersByJobTask: PropTypes.object,
  setExpandedTasks: PropTypes.func,
  workspacesMetadata: PropTypes.array,
  onRefreshWorkspacesMetadata: PropTypes.func,
  onProjectAdded: PropTypes.func,
  selectedFilters: PropTypes.object,
  onPriorityUpdate: PropTypes.func,
  defWorkId: PropTypes.any,
  onOpenModal: PropTypes.func.isRequired,  
  workspaceRole: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

WorkspaceStack.defaultProps = {
  stackedJobs: [],
  expandedStacks: {},
  toggleStackExpansion: () => {},
  searchTerm: '',
  hasActiveFilters: false,
  openAiDialogForJob: () => {},
  renderStackedImageIcon: () => null,
  TASK_STATUSES: {},
  ALL_TASKS_TAB: {},
  handleRowClick: () => {},
  handleRowDoubleClick: () => {},
  handleAddFromRow: () => {},
  handleEdit: () => {},
  handleDelete: () => {},
  handleViewAttachments: () => {},
  handleReplyToNote: () => {},
  handlePriorityClick: () => {},
  handlePriorityMouseEnter: () => {},
  handlePriorityMouseLeave: () => {},
  handleNoteTextMouseEnter: () => {},
  handleNoteTextMouseLeave: () => {},
  handleLinkedNoteClick: () => {},
  handleLinkedNoteMouseEnter: () => {},
  handleLinkedNoteMouseLeave: () => {},
  isNoteReply: () => false,
  getReplyNoteId: () => null,
  isOriginalNoteExists: () => false,
  userStatusMap: {},
  loadingUsers: {},
  getPriorityValue: () => null,
  manuallyUpdatedPriorities: {},
  shouldShowNotePopup: () => false,
  tasksByJob: {},
  loadingTasksByJob: {},
  expandedTasks: {},
  toggleTaskExpansion: () => {},
  notesByTask: {},
  loadingNotesByTask: {},
  fetchTasksForJob: () => {},
  fetchNotesForTask: () => {},
  onTaskEdit: () => {},
  onTaskDelete: () => {},
  onTaskStatusChange: () => {},
  onTaskClick: () => {},
  refreshTasksForJob: () => {},
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  onOpenEdit: () => {},
  onCreateTask: () => {},
  onOpenSlideshow: () => {},
  onTabChange: () => {},
  onAssigneeFilter: () => {},
  clearAssigneeFilter: () => {},
  fetchJobPermissionUsers: () => {},
  onDenyUserClick: () => {},
  fetchJobTaskUsers: () => {},
  jobUsersTask: {},
  loadingUsersByJobTask: {},
  setExpandedTasks: () => {},
  workspacesMetadata: [],
  onRefreshWorkspacesMetadata: () => {},
  onProjectAdded: () => {},
  selectedFilters: {},
  onPriorityUpdate: () => {},
};

export default WorkspaceStack;